import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Calculator, Phone, MessageCircle, Car } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api, fallbackData, companyInfo } from '../data/api';

const PriceSection = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);
  
  // KM Calculator state
  const [km, setKm] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  const KM_RATE = 40; // TL per km
  const OPENING_FEE = 50; // TL açılış ücreti

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const data = await api.getPrices();
        if (data && data.length > 0) {
          setPrices(data);
        } else {
          setPrices(fallbackData.prices);
        }
      } catch (error) {
        setPrices(fallbackData.prices);
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, []);

  const handleCall = () => {
    window.location.href = `tel:${companyInfo.phoneClean}`;
  };

  const handleWhatsApp = (route) => {
    const message = route 
      ? encodeURIComponent(`Merhaba, ${route.from} - ${route.to} arası taksi ücreti hakkında bilgi almak istiyorum.`)
      : encodeURIComponent(companyInfo.whatsappMessage);
    window.open(`https://wa.me/${companyInfo.phoneClean.replace('+', '')}?text=${message}`, '_blank');
  };

  const handleWhatsAppWithPrice = () => {
    const message = encodeURIComponent(`Merhaba, ${km} km mesafe için taksi ücreti sormak istiyorum. Hesaplamama göre yaklaşık ${calculatedPrice} TL.`);
    window.open(`https://wa.me/${companyInfo.phoneClean.replace('+', '')}?text=${message}`, '_blank');
  };

  const calculatePrice = () => {
    const distance = parseFloat(km);
    if (distance > 0) {
      const total = (distance * KM_RATE) + OPENING_FEE;
      setCalculatedPrice(Math.round(total));
    } else {
      setCalculatedPrice(null);
    }
  };

  const handleKmChange = (e) => {
    const value = e.target.value;
    // Sadece sayı ve nokta kabul et
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setKm(value);
      if (value) {
        const distance = parseFloat(value);
        if (distance > 0) {
          const total = (distance * KM_RATE) + OPENING_FEE;
          setCalculatedPrice(Math.round(total));
        } else {
          setCalculatedPrice(null);
        }
      } else {
        setCalculatedPrice(null);
      }
    }
  };

  return (
    <section id="prices" className="py-20 md:py-28 bg-zinc-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-amber-900/10 via-zinc-950 to-zinc-950"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-amber-400 text-sm font-semibold tracking-wider uppercase mb-4">
            Fiyat Tahmini
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Denizli Taksi <span className="text-amber-400">Ücretleri</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Popüler güzergahlar için tahmini fiyatlarımız. Kesin fiyat için lütfen bizi arayın.
          </p>
        </div>

        {/* Price Cards */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-zinc-900/80 rounded-2xl p-6 border border-zinc-800 animate-pulse">
                <div className="h-16 bg-zinc-800 rounded mb-4"></div>
                <div className="h-8 bg-zinc-800 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prices.map((route, index) => (
              <div
                key={route.id || index}
                onClick={() => setSelectedRoute(selectedRoute === index ? null : index)}
                className={`cursor-pointer bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border transition-all duration-300 hover:shadow-xl ${
                  selectedRoute === index 
                    ? 'border-amber-500 shadow-lg shadow-amber-500/20' 
                    : 'border-zinc-800 hover:border-amber-500/50'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
                      <MapPin className="w-4 h-4 text-amber-400" />
                      <span>Kalkış</span>
                    </div>
                    <p className="text-white font-semibold text-lg">{route.from}</p>
                  </div>
                  <div className="text-amber-400 text-2xl">→</div>
                  <div className="flex-1 text-right">
                    <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1 justify-end">
                      <span>Varış</span>
                      <MapPin className="w-4 h-4 text-green-400" />
                    </div>
                    <p className="text-white font-semibold text-lg">{route.to}</p>
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Tahmini Ücret</p>
                      <p className="text-2xl font-bold text-amber-400">{route.price}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Süre</p>
                      <div className="flex items-center gap-1 text-zinc-300">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">{route.duration}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedRoute === index && (
                  <div className="mt-4 pt-4 border-t border-zinc-800 flex gap-3 animate-in fade-in duration-300">
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleCall(); }}
                      className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                      size="sm"
                    >
                      <Phone className="w-4 h-4 mr-1" /> Ara
                    </Button>
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleWhatsApp(route); }}
                      className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                      size="sm"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* KM Calculator */}
        <div className="mt-16 max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-amber-500/20 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Calculator className="w-8 h-8 text-zinc-900" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">KM ile Fiyat Hesapla</h3>
              <p className="text-zinc-400 text-sm">Mesafeyi girin, tahmini ücreti öğrenin</p>
            </div>

            <div className="space-y-6">
              {/* Input */}
              <div className="relative">
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  value={km}
                  onChange={handleKmChange}
                  placeholder="Örn: 15"
                  className="bg-zinc-900/50 border-zinc-700 text-white text-2xl md:text-3xl text-center py-6 h-16 placeholder:text-zinc-500 focus:border-amber-500 pr-16 w-full"
                  style={{ fontSize: '24px', minWidth: '100%' }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-400 font-semibold text-lg">KM</span>
              </div>

              {/* Tariff Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center border border-zinc-700/50">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Açılış Ücreti</p>
                  <p className="text-xl font-bold text-white">{OPENING_FEE} TL</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center border border-zinc-700/50">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">KM Başı</p>
                  <p className="text-xl font-bold text-white">{KM_RATE} TL</p>
                </div>
              </div>

              {/* Result */}
              {calculatedPrice !== null && (
                <div className="bg-gradient-to-r from-amber-500/20 to-amber-600/20 rounded-2xl p-6 border border-amber-500/30 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="text-center">
                    <p className="text-zinc-400 text-sm mb-2">
                      {km} km için tahmini ücret
                    </p>
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Car className="w-6 h-6 text-amber-400" />
                      <span className="text-4xl md:text-5xl font-bold text-amber-400">{calculatedPrice} TL</span>
                    </div>
                    <p className="text-zinc-500 text-xs mb-4">
                      ({OPENING_FEE} TL açılış + {km} km × {KM_RATE} TL = {calculatedPrice} TL)
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button
                        onClick={handleCall}
                        className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                      >
                        <Phone className="w-4 h-4 mr-1" /> Hemen Ara
                      </Button>
                      <Button
                        onClick={handleWhatsAppWithPrice}
                        className="bg-green-600 hover:bg-green-500 text-white"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SEO Note */}
        <p className="text-center text-zinc-600 text-xs mt-8">
          * Fiyatlar tahmini olup trafik durumu ve güzergaha göre değişiklik gösterebilir. 
          Denizli taksi ücreti için güncel bilgi almak için lütfen arayınız.
        </p>
      </div>
    </section>
  );
};

export default PriceSection;
